import { useCallback, useContext, useState } from "react";

import { useGetCustomerPaymentMethods } from "~/features/payment/hooks/use-get-customer-payment-methods.ts";
import { mapPaymentErrorToUserMessage } from "~/features/payment/utils/payment-error-mapper.ts";
import type { SelectedPaymentMethod } from "~/features/payment/utils/payment-method-helpers.ts";
import {
	buildPrepayPayment,
	getMethodAccountType,
	getMethodLastFour,
} from "~/features/payment/utils/payment-method-helpers.ts";
import { usePrepaidPaymentForTender } from "~/features/quote/hooks/use-prepaid-payment-for-tender.ts";
import {
	useSendPrepayPaymentSuccessEmail,
	useSendPrepayTenderFailureEmail,
} from "~/features/quote/hooks/use-prepay-notifications.ts";
import { TENDER_ERROR_MESSAGE } from "~/features/quote/hooks/use-shipment-summary-hooks.tsx";
import { useIsVendor } from "~/shared/hooks/use-is-vendor.ts";
import type {
	PrepayCustomer,
	PrepayQuoteBol,
} from "~/shared/schemas/prepay-notification-schema.ts";
import type { QuoteResponse } from "~/shared/schemas/quote-schema.ts";
import { sendGoogleAdsConversionEvent } from "~/shared/utils/google-analytics-helper.ts";
import { useIsAuthenticated } from "~/shared/utils/use-is-authenticated.tsx";

import { QuoteDetailsContext } from "../../quote/context/quote-details-context.tsx";

type PendingPayment = {
	quote: QuoteResponse;
	paymentMethod: SelectedPaymentMethod;
	amount: number;
};

type UsePrepayTenderFlowOptions = {
	readonly onPaymentError: (message: string) => void;
	readonly onTenderSuccess: (quote: QuoteResponse) => void;
	readonly onTenderError: (message: string) => void;
	readonly submitTenderQuote: (quote: QuoteResponse) => Promise<{
		status: number;
		data?: QuoteResponse;
		message?: string;
	}>;
};

export const usePrepayTenderFlow = ({
	onPaymentError,
	onTenderSuccess,
	onTenderError,
	submitTenderQuote,
}: UsePrepayTenderFlowOptions) => {
	const { quoteState } = useContext(QuoteDetailsContext);
	// Vendors are not allowed to prepay, so never fetch their saved payment
	// methods/accounts. This keeps /payments/get-payment-methods and
	// /payments/pay-accounts from firing for vendors (e.g. on /ltl-shipment).
	const { isVendor } = useIsVendor();
	const canLoadPaymentData = !isVendor;
	const { runPrepaidPayment, isPaying, calculateQuoteTotal } =
		usePrepaidPaymentForTender({ enabled: canLoadPaymentData });
	const { mutateAsync: sendSuccessEmail } = useSendPrepayPaymentSuccessEmail();
	const { mutateAsync: sendFailureEmail } = useSendPrepayTenderFailureEmail();
	const { paymentMethods } = useGetCustomerPaymentMethods({
		enabled: canLoadPaymentData,
	});
	// Legacy populates the notification's customer.email with the logged-in
	// user's email (CustomerDetails carries no email of its own), so mirror
	// that here.
	const { email: authenticatedEmail } = useIsAuthenticated();

	const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
	const [pendingTenderQuote, setPendingTenderQuote] =
		useState<QuoteResponse | null>(null);
	const [selectedPaymentValue, setSelectedPaymentValue] =
		useState<SelectedPaymentMethod | null>(null);
	const [pendingPayment, setPendingPayment] = useState<PendingPayment | null>(
		null,
	);
	// Covers the whole post-confirmation sequence (payment -> success email ->
	// final tender). `isPaying` only tracks the Braintree charge, so without this
	// the global spinner would drop out during the email + final tender calls.
	const [isFinalizing, setIsFinalizing] = useState(false);

	const startPrepayFlow = useCallback((quote: QuoteResponse) => {
		setPendingTenderQuote(quote);
		setShowPaymentMethodModal(true);
	}, []);

	const buildFailureEmailBody = useCallback(
		(): PrepayCustomer => ({
			customerName: quoteState.customer?.name ?? "",
			customerEmail: authenticatedEmail ?? "",
			brokerEmail: quoteState.customer?.primaryBroker?.email ?? "",
			ltlBrokerEmail: quoteState.customer?.ltlBroker?.email ?? "",
		}),
		[quoteState.customer, authenticatedEmail],
	);

	// Step 2.4 — User picked a payment method; capture intent and open
	// the confirmation dialog rather than submitting immediately.
	const handleApplyPaymentMethod = useCallback(
		(paymentMethod: SelectedPaymentMethod | null) => {
			setSelectedPaymentValue(paymentMethod);
			if (!paymentMethod || !pendingTenderQuote) {
				return;
			}
			const quote = pendingTenderQuote;
			const amount = calculateQuoteTotal(quote);
			setShowPaymentMethodModal(false);
			setPendingPayment({ quote, paymentMethod, amount });
		},
		[pendingTenderQuote, calculateQuoteTotal],
	);

	const handleClosePaymentMethodModal = useCallback(() => {
		setShowPaymentMethodModal(false);
		setPendingTenderQuote(null);
	}, []);

	const handleCancelConfirmation = useCallback(() => {
		setPendingPayment(null);
		setPendingTenderQuote(null);
	}, []);

	const handleConfirmPayment = useCallback(() => {
		if (!pendingPayment) return;
		const { quote, paymentMethod, amount } = pendingPayment;
		setPendingPayment(null);
		setPendingTenderQuote(null);
		setIsFinalizing(true);

		void (async () => {
			try {
				let paymentResponse: { confirmationNumber?: string } = {};
				try {
					const result = (await runPrepaidPayment({
						quote,
						paymentMethod,
					})) as {
						confirmationNumber?: string;
					} | null;
					paymentResponse = result ?? {};
				} catch (error) {
					// Phase 4b — payment failed; fire failure email best-effort
					// then surface the error to the user.
					try {
						await sendFailureEmail(buildFailureEmailBody());
					} catch (emailError) {
						console.error(
							"Pre-pay tender failure email failed to send:",
							emailError,
						);
					}
					onPaymentError(mapPaymentErrorToUserMessage(error));
					return;
				}

				// Phase 4a — payment succeeded; fire success email before final
				// tender call. Failure here does not block tendering.
				const prepayPayment = buildPrepayPayment(
					paymentMethod,
					quote,
					amount,
					"",
					paymentResponse.confirmationNumber ?? "",
				);
				const customer = quoteState.customer;
				const bolBody: PrepayQuoteBol = {
					quoteId: Number(quote.id),
					customer: {
						name: customer?.name ?? "",
						email: authenticatedEmail ?? "",
						brokerName: customer?.primaryBroker?.name ?? "",
						ltlBrokerEmail: customer?.ltlBroker?.email ?? "",
						brokerEmail: customer?.primaryBroker?.email ?? "",
						ltlBrokerId: customer?.ltlBroker?.id ?? 0,
						brokerId: customer?.primaryBroker?.id ?? 0,
						id: customer?.id ?? 0,
					},
					payment: prepayPayment,
					// Legacy sends no recipients and a hardcoded 0 cost; the BFF
					// resolves the recipients and email content server-side.
					toEmails: [],
					tqlpo: quote.poNumber ?? 0,
					customerCost: 0,
				};
				try {
					await sendSuccessEmail(bolBody);
				} catch (emailError) {
					console.error(
						"Pre-pay tender success email failed to send:",
						emailError,
					);
				}

				// Phase 5 — final tender call.
				const finalResult = await submitTenderQuote(quote);
				if (finalResult.status !== 200 || !finalResult.data) {
					onTenderError(finalResult.message ?? TENDER_ERROR_MESSAGE);
					return;
				}

				// Phase 6.1 — Google Ads conversion event (prepay tenders only).
				const bookedCarrier = quoteState.carrierPrices.find(
					(c) => c.id === quoteState.selectedCarrierPriceId,
				);
				if (bookedCarrier) {
					sendGoogleAdsConversionEvent(
						String(bookedCarrier.customerRate),
						String(quote.poNumber ?? ""),
					);
				}

				onTenderSuccess(finalResult.data);
			} finally {
				setIsFinalizing(false);
			}
		})();
	}, [
		pendingPayment,
		runPrepaidPayment,
		sendSuccessEmail,
		sendFailureEmail,
		buildFailureEmailBody,
		quoteState.customer,
		quoteState.carrierPrices,
		quoteState.selectedCarrierPriceId,
		authenticatedEmail,
		submitTenderQuote,
		onPaymentError,
		onTenderError,
		onTenderSuccess,
	]);

	const formattedAmount = pendingPayment
		? pendingPayment.amount.toLocaleString("en-US", {
				style: "currency",
				currency: "USD",
			})
		: "";
	const confirmLastFour = pendingPayment
		? getMethodLastFour(pendingPayment.paymentMethod)
		: "";
	const confirmAccountType = pendingPayment
		? getMethodAccountType(pendingPayment.paymentMethod)
		: "";

	return {
		// state
		paymentMethods,
		selectedPaymentValue,
		showPaymentMethodModal,
		pendingPayment,
		isPaying,
		// True for the entire confirm sequence (payment + email + final tender),
		// so consumers can keep one spinner up across all three calls.
		isProcessing: isPaying || isFinalizing,
		formattedAmount,
		confirmLastFour,
		confirmAccountType,
		// actions
		startPrepayFlow,
		handleApplyPaymentMethod,
		handleClosePaymentMethodModal,
		handleCancelConfirmation,
		handleConfirmPayment,
	};
};

export type PrepayTenderFlow = ReturnType<typeof usePrepayTenderFlow>;
